import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Composer, Container, Header, MessageList, useWebchat } from "@botpress/webchat";
import { Edit } from "@mui/icons-material";
import { Box, Divider, Stack, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import AssessmentSelectionDialog from "../dialogs/assessmentSelectionDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import Checkbox from "../ui/omniCheckbox";
import OmniMenuItem from "../ui/omniMenuItem.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import OmniTextField from "../ui/omniTextField.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressTestHarness = ({
  botpressBotODM,
  userODM,
  widgetSize = "large",
  minHeight = "333px",
  loadConversationRequest,
  deleteChatRequest,
  conversation,
  conversationFeedback,
  deleteFeedbackRequest,
  updateFeedbackRequest,
  updateAssessmentRequest,
}) => {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const webchatClientID = botpressBotODM?.omniData.webchatClientId;

  const calculateUserData = (theData, userODM) => {
    try {
      //try is excessive but saftey first
      if (userODM && theData) {
        theData.firstName = userODM.omniData.givenName;
        theData.lastName = userODM.omniData.familyName;
      }
    } catch (error) {}
    return theData;
  };

  const [userData, setUserData] = useState(
    calculateUserData(botpressBotODM?.omniData.userData, userODM),
  );

  const [editUserData, setEditUserData] = useState(null);

  const [botData, setBotData] = useState(botpressBotODM?.omniData.botData);
  const [editBotData, setEditBotData] = useState(null);

  const [languageCode, setLanguageCode] = useState(botpressBotODM?.omniData.userData.languageCode);

  const botDisplayName = botpressBotODM?.omniData.botData?.botName;

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const [newConversationOnLaunch, setNewConversationOnLaunch] = useState(true);

  const { client, on, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: true,
    storageKey: `botpress-testHarness-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const endConversation = () => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
        enqueueSnackbar("Any Feedback you left will be available in a few minutes", {
          variant: "success",
        });
      });
    } catch (error) {
      console.error("Error ending conversation:", error);
    }
  };

  const endAssessment = (conversationId, messageCount, elapsedSeconds) => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
      });

      enqueueSnackbar("Finalizing the conversation for you to give feedback...", {
        variant: "success",
      });
      //store conversation id to load feedback dialog
      //create new feedback
      const theRecord = {
        id: null,
        comment: "",
        questionCount: messageCount,
        archived: false,
        userName: userODM
          ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
          : "Unknown User",
        conversationId: conversationId,
        elapsedSeconds: elapsedSeconds,
        assessmentType: selectedAssessmentTemplate
          ? selectedAssessmentTemplate.omniData.omniType
          : "Custom",
        assessmentName: selectedAssessmentTemplate
          ? selectedAssessmentTemplate.omniData.title
          : "Custom Assessment",
      };
      // "Run conversation" clean up
      setMessageArray(null);
      setQuestionIndex(-1);
      setAssessmentInProgress(false);

      // Normal cleanup
      updateAssessmentRequest({ assessmentRecord: theRecord });
      setPostAssessmentDisplay(true);
      loadConversationRequest(conversationId);
      selectedAssessmentTemplate(null);
    } catch (error) {
      console.error("Error ending assessment:", error);
    }
  };

  const startWebchat = () => {
    if (newConversationOnLaunch) {
      newConversation().then(() => {
        setIsWebchatOpen(true);
      });
    } else {
      setIsWebchatOpen(true);
    }
  };

  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.includes("text") && !file.name.endsWith(".txt")) {
      enqueueSnackbar("Please select a text file (.txt)", {
        variant: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = readerEvent => {
      try {
        const fileContent = readerEvent.target?.result;
        if (!fileContent || typeof fileContent !== "string") {
          enqueueSnackbar("Unable to read file contents", {
            variant: "error",
          });
          return;
        }

        // Split file into lines, filter empty lines
        const lines = fileContent
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (lines.length === 0) {
          enqueueSnackbar("File is empty. Please select a file with content.", {
            variant: "error",
          });
          return;
        }

        // Start the conversation with file contents
        loadConversationRequest(null);
        setLastMessage(null);
        setAssessmentInProgress(true);
        setMessageArray(lines);
        startWebchat();
        setCountdown(10);
      } catch (error) {
        console.error("Error reading file:", error);
        enqueueSnackbar("Error reading file. Please try again.", {
          variant: "error",
        });
      }
    };
    reader.readAsText(file);
  };

  const [userInitialized, SetUserInitialized] = useState(false);

  useEffect(() => {
    if (client && userData && !userInitialized) {
      try {
        client.updateUser({
          data: userData,
        });
        SetUserInitialized(true);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, userData, userInitialized]);

  //#region: Auto Ask Questions

  const [postAssessmentDisplay, setPostAssessmentDisplay] = useState(false);
  const [selectAssessment, setSelectAssessment] = useState(false);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const [messageArray, setMessageArray] = useState(null);
  const [countdown, setCountdown] = useState(-1);
  const [startTime, setStartTime] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(-1);
  const [fileInputRef, setFileInputRef] = useState(null);

  const [selectedAssessmentTemplate, setSelectedAssessmentTemplate] = useState(null);

  useEffect(() => {
    if (messageArray && countdown < 0 && questionIndex >= 0) {
      if (questionIndex >= messageArray.length) {
        let conversationId = null;
        if (messages.length > 0) {
          const lastMessage = messages[0];
          conversationId = lastMessage.conversationId;
        }
        let messageCount = 0;
        messageCount = messageArray.length;

        setLastMessage(null);
        setMessageArray(null);
        setQuestionIndex(-1);
        setAssessmentInProgress(false);

        const endTime = Date.now();
        const elapsedSeconds = Math.floor((endTime - startTime) / 1000);

        endAssessment(conversationId, messageCount, elapsedSeconds);
      } else {
        let theText = messageArray[questionIndex];
        client?.sendMessage({
          type: "text",
          text: theText,
        });
      }
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIndex, messageArray, countdown]);

  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    const unsubscribe = on("message", message => {
      if (message.authorId !== user?.userId && countdown < 0) {
        try {
          setLastMessage(message);
        } catch (error) {
          console.error("Error in TTS message handling:", error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [on, user, countdown]);

  useEffect(() => {
    //ok start the automated process
    if (lastMessage && questionIndex >= 0) {
      const delayGotoNextQuestion = setTimeout(() => {
        //todo this just got harder.

        setQuestionIndex(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(delayGotoNextQuestion);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  useEffect(() => {
    //ok start the automated process
    if (countdown > 0) {
      const countdownFunction = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(countdownFunction);
    } else if (countdown === 0) {
      setStartTime(Date.now());
      setQuestionIndex(0);
      setCountdown(-1);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);
  //#endregion

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      {!isWebchatOpen && (
        <Widget widgetSize={"full"} minHeight={"100%"}>
          <WidgetTitle title={"Launch Pad"} text={botDisplayName}>
            <OmniResponsiveButton
              tooltipTitle="Edit the User Settings"
              buttonText="User"
              sx={{ marginRight: theme.spacing(2) }}
              onClick={() => {
                setEditUserData(JSON.stringify(userData, null, 2));
              }}
              icon={<Edit />}
            />
            <OmniResponsiveButton
              tooltipTitle="Edit the AI Settings"
              buttonText="Bot"
              onClick={() => {
                setEditBotData(JSON.stringify(botData, null, 2));
              }}
              icon={<Edit />}
            />
          </WidgetTitle>
          <WidgetContent>
            <Grid container size={{ xs: 12 }}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    marginRight: theme.spacing(2),
                    marginLeft: theme.spacing(2),
                    gap: theme.spacing(2),
                    padding: theme.spacing(1),
                  }}
                >
                  <OmniTextField
                    label="Initial Language"
                    select
                    variant="outlined"
                    id={"language"}
                    value={languageCode}
                    onChange={event => {
                      setLanguageCode(event.target.value);
                      let newUserData = JSON.parse(userData);
                      newUserData.languageCode = event.target.value;
                      setUserData(JSON.stringify(newUserData, null, 2));
                    }}
                    placeholder={"language"}
                    required
                  >
                    <OmniMenuItem key={"en-US"} value={"en-US"}>
                      {"English (US)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"es-US"} value={"es-US"}>
                      {"Spanish (US)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"ru-RU"} value={"ru-RU"}>
                      {"Russian (RU)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"sw-KE"} value={"sw-KE"}>
                      {"Swahili (KE)"}
                    </OmniMenuItem>
                  </OmniTextField>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    marginRight: theme.spacing(2),
                    marginLeft: theme.spacing(2),
                    marginTop: theme.spacing(1),

                    gap: theme.spacing(2),
                    padding: theme.spacing(1),
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={newConversationOnLaunch}
                          onChange={() => setNewConversationOnLaunch(!newConversationOnLaunch)}
                          name="newConversationOnLaunch"
                        />
                      }
                      label="Always start with a new conversation."
                    />
                  </FormGroup>
                </Box>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    margin: theme.spacing(2),
                    gap: theme.spacing(2),
                    padding: theme.spacing(1),

                    alignSelf: "flex-end",
                    justifyContent: "flex-end",
                    textAlign: "right",
                  }}
                >
                  <Button
                    sx={{ width: "100%", height: "100" }}
                    tooltipTitle="Start Conversation"
                    variant="contained"
                    onClick={() => {
                      startWebchat();
                    }}
                  >
                    <Stack>
                      <Typography
                        variant="h4"
                        sx={{
                          borderBottom: `2px solid `,
                          margin: theme.spacing(2),
                          paddingBottom: theme.spacing(2),
                          marginBottom: 0,
                        }}
                      >
                        Start Conversation
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          margin: theme.spacing(2),
                        }}
                      >
                        Click Here
                      </Typography>
                    </Stack>
                  </Button>

                  <Divider xs={{ margin: theme.spacing(2) }} />

                  <Button
                    sx={{ width: "100%", height: "100" }}
                    tooltipTitle="Start Conversation"
                    variant="contained"
                    onClick={() => {
                      setSelectAssessment(true);
                    }}
                  >
                    <Stack>
                      <Typography
                        variant="h4"
                        sx={{
                          borderBottom: `2px solid `,
                          margin: theme.spacing(2),
                          paddingBottom: theme.spacing(2),
                          marginBottom: 0,
                        }}
                      >
                        Bot Assessments
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          margin: theme.spacing(2),
                        }}
                      >
                        Click Here
                      </Typography>
                    </Stack>
                  </Button>

                  <Divider xs={{ margin: theme.spacing(2) }} />

                  <Button
                    sx={{ width: "100%", height: "100" }}
                    tooltipTitle="Run a conversation from a text file"
                    variant="contained"
                    onClick={() => {
                      fileInputRef?.click();
                    }}
                  >
                    <Stack>
                      <Typography
                        variant="h4"
                        sx={{
                          borderBottom: `2px solid `,
                          margin: theme.spacing(2),
                          paddingBottom: theme.spacing(2),
                          marginBottom: 0,
                        }}
                      >
                        Run Conversation
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          margin: theme.spacing(2),
                        }}
                      >
                        Click Here
                      </Typography>
                    </Stack>
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </WidgetContent>
        </Widget>
      )}
      {isWebchatOpen && (
        <Container
          connected={clientState !== "disconnected"}
          style={{
            height: "755px",
            display: isWebchatOpen ? "flex" : "none",
          }}
        >
          <Header
            // onOpenChange={() => console.log('Override the header open change')}
            defaultOpen={false}
            closeWindow={() => endConversation()}
            // closeWindow={() => setIsWebchatOpen(false)}
            // restartConversation={newConversation}
            configuration={botData}
          />
          <MessageList
            botAvatar={botData?.botAvatar}
            botName={botData?.botName}
            botDescription={botData?.botDescription}
            isTyping={isTyping}
            headerMessage="Chat History"
            showMarquee={true}
            messages={enrichedMessages}
            sendMessage={client?.sendMessage}
            addMessageFeedback={client?.addMessageFeedback}
          />
          <Composer
            disableComposer={false}
            isReadOnly={false}
            allowFileUpload={true}
            connected={clientState !== "disconnected"}
            sendMessage={client?.sendMessage}
            uploadFile={client?.uploadFile}
            composerPlaceholder="Type a message..."
          />
        </Container>
      )}

      {assessmentInProgress && (
        <Box
          style={{
            position: "absolute",
            zIndex: 9999,
            height: "755px",
            display: "flex",
            color: "black",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            width: "100%",
            alignItems: "center",
            // justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Box
            style={{
              // height: "755px",
              display: "flex",
              padding: theme.spacing(4),
              borderRadius: theme.spacing(2),
              marginTop: theme.spacing(10),
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              boxShadow: theme.shadows[3],
            }}
          >
            <Typography
              variant="h3"
              sx={{
                marginBottom: theme.spacing(2),
              }}
            >
              {countdown > 0 ? "Starting Assessment" : "Performing Assessment"}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                marginBottom: theme.spacing(2),
              }}
            >
              {selectedAssessmentTemplate?.omniData.title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                marginBottom: theme.spacing(2),
              }}
            >
              {botpressBotODM.omniData.name}
            </Typography>
            {countdown > 0 && (
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  magin: theme.spacing(2),

                  background: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  border: `4px solid ${theme.palette.secondary.contrastText}`,
                }}
              >
                <Typography variant="h2">{countdown}</Typography>
              </Avatar>
            )}
            {countdown < 0 && messageArray && (
              <Typography
                variant="h6"
                sx={{
                  marginBottom: theme.spacing(2),
                }}
              >
                {`Asking Question ${questionIndex + 1} of ${messageArray.length}`}
              </Typography>
            )}
            {countdown < 0 && messageArray && (
              <LinearProgress
                variant="determinate"
                color="secondary"
                sx={{
                  width: "80%",
                  height: theme.spacing(2),
                  borderRadius: theme.spacing(1),
                  boxShadow: theme.shadows[3],
                }}
                value={((questionIndex + 1) / messageArray.length) * 100}
              />
            )}
          </Box>
        </Box>
      )}

      {editUserData && (
        <TextInputDialog
          inputLabel={`User Data JSON`}
          open={editUserData}
          dialogTitle={"Update User Data"}
          dialogMessage={`Paste in some well formed JSON.`}
          defaultValue={editUserData}
          inputPlaceholder={"User Data JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={10}
          onAccept={theValue => {
            try {
              //validate
              setUserData(theValue);
              setEditUserData(null);
            } catch (error) {
              setEditUserData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditUserData(null);
          }}
        />
      )}

      {editBotData && (
        <TextInputDialog
          inputLabel={`Bot Configuration JSON`}
          open={editBotData}
          dialogTitle={"Update Bot Configuration"}
          dialogMessage={`Paste in some well formed JSON.`}
          defaultValue={editBotData}
          inputPlaceholder={"Bot Configuration JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={15}
          onAccept={theValue => {
            try {
              //validate
              setBotData(theValue);
              setEditBotData(null);
            } catch (error) {
              setEditBotData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditBotData(null);
          }}
        />
      )}

      {selectAssessment && (
        <AssessmentSelectionDialog
          open={selectAssessment}
          onAccept={assessment => {
            const theQuestions = [];
            for (let i = 0; i < assessment.omniData.questions.length; ++i) {
              theQuestions.push(assessment.omniData.questions[i].text);
            }
            setSelectedAssessmentTemplate(assessment);
            loadConversationRequest(null);
            setLastMessage(null);
            setAssessmentInProgress(true);
            setMessageArray(theQuestions);
            startWebchat();
            setCountdown(10);
            setSelectAssessment(false);
          }}
          onClose={() => {
            setSelectAssessment(false);
          }}
        />
      )}

      <input
        ref={el => setFileInputRef(el)}
        type="file"
        accept=".txt,text/plain"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {conversation != null && postAssessmentDisplay && (
        <BotpressConversationDialog
          conversationId={conversation.conversationId}
          conversation={conversation}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          userODM={userODM}
          open={conversation != null}
          defaultValue={conversation}
          onAccept={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: conversation.id });
            loadConversationRequest(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
            setPostAssessmentDisplay(false);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  botpressBotODM: state.botpressSession.botpressBotODM,
  userODM: state.userSession.userODM,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,
});

const mapDispatchToProps = {
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  updateAssessmentRequest: BotpressSessionActions.updateAssessmentRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressTestHarness));
